import { MaybeExportedTree, Tree, TreeNode } from "@/types/tree";
import {
  deleteNode,
  exportTree,
  findNode,
  getBlankTree,
  insertNewNodeAt,
  treesEqual
} from "@/lib/tree/tree";
import { configStore, documentStore, uiStore } from "@/store/store";
import { GlobalBlock } from "@/types/global-blocks";
import defensiveCopy from "@/util/defensive-copy";
import { saveGlobalBlockToWordPress } from "@/lib/global-blocks/global-blocks";
import { isNodeADescendantOf } from "@/lib/tree/nesting";
import { breakdanceUndo } from "@/lib/undo/undo";

export function insertNodesInInlineBlock(
  parentId: TreeNode["id"],
  block: GlobalBlock
) {
  const nodes = formatTreeNodesForInlineEditing(block.id, block?.tree);
  const parentNode = findNode(documentStore.document.tree, parentId);

  if (parentNode?.children.length) {
    console.log(
      "Parent node already has children, this could be an undo transaction.",
      parentNode.children
    );
    return;
  }

  breakdanceUndo.transaction(
    () => {
      nodes.forEach((node, index) =>
        insertNewNodeAt(documentStore.document.tree, node, parentId, index)
      );
    },
    "Start Inline Editing",
    `inline-editing:${parentId}`
  );

  return nodes;
}

export function getNodesFromInlineBlock(element: TreeNode) {
  return defensiveCopy(element.children).map(removePostIdFromNodeId);
}

export function pullNodesFromInlineBlock(element: TreeNode) {
  const nodes = getNodesFromInlineBlock(element);

  const existingNodes = element.children.map(child => child.id);

  existingNodes.forEach(childId =>
    deleteNode(documentStore.document.tree, childId)
  );

  return nodes;
}

export async function updateBlockTree(
  block: GlobalBlock,
  elementId: TreeNode["id"],
  children: TreeNode[],
  _nextNodeId?: number
) {
  const blankTree = getBlankTree();

  const updatedTree = block?.tree
    ? defensiveCopy({
        ...block.tree,
        root: {
          ...block.tree.root,
          children
        }
      })
    : exportTree({
        ...blankTree,
        root: {
          ...blankTree.root,
          children
        }
      });

  if (_nextNodeId) {
    updatedTree._nextNodeId = _nextNodeId;
  }

  if (block.tree && treesEqual(updatedTree, block.tree)) {
    return;
  }

  // Show loading state
  uiStore.setElementUpdatingState({
    elementId,
    isUpdating: true
  });

  // Update blocks in store
  const newBlocks = configStore.globalBlocks.map(b =>
    b.id === block.id ? { ...b, tree: updatedTree } : b
  );

  configStore.setGlobalBlocks(newBlocks);

  // And save to WordPress
  await saveGlobalBlockToWordPress({
    ...block,
    tree: updatedTree
  });

  uiStore.setElementUpdatingState({
    elementId,
    isUpdating: false
  });

  // Trigger a re-render of the SSR element
  uiStore.setRefreshSsrElementId(elementId);
}

// We put the nodes from the global block within the current tree, because they might share similar IDs,
// we need to make sure they are unique, so we add the post ID to the node ID.
// We do this solely to avoid changing the existing IDs and potentially breaking references.
export function addPostIdToNodeId(
  postId: TreeNode["id"],
  node: TreeNode
): TreeNode {
  return {
    ...node,
    id: `${postId}-${node.id}`,
    children: node.children
      ? node.children.map(child => addPostIdToNodeId(postId, child))
      : node.children
  };
}

export function removePostIdFromNodeId(node: TreeNode): TreeNode {
  return {
    ...node,
    id: typeof node.id === "string" ? Number(node.id.split("-")[1]) : node.id,
    children: node.children
      ? node.children.map(child => removePostIdFromNodeId(child))
      : node.children
  };
}

export function formatTreeNodesForInlineEditing(
  postId: TreeNode["id"],
  tree?: MaybeExportedTree | null
): TreeNode[] {
  if (!tree || tree.root.children === undefined) return [];
  return tree.root.children.map(child => addPostIdToNodeId(postId, child));
}

export function isNodeDisallowedInsideWhenInlineEditingIsEnabled(
  tree: Tree,
  node: TreeNode
) {
  const elementId = uiStore.inlineEditingContext?.elementId;

  // Bail out if we're not editing an element
  if (!elementId) return false;

  // Making an assumption here
  const inlineNodeBeingEdited = findNode(tree, elementId)!;

  // If the node is a descendant of the inline node being edited,
  // it's allowed to move around. Otherwise, you can't drag it outside the inline node.
  const isAllowed =
    isNodeADescendantOf(tree, node, inlineNodeBeingEdited) ||
    node.id === elementId;

  return !isAllowed;
}

export function getInlineBlockPreviewableItemUrl() {
  if (!uiStore.inlineEditingContext?.blockId) return null;

  const block = configStore.globalBlocks.find(
    block => block.id === uiStore.inlineEditingContext?.blockId
  );

  return block?.lastPreviewedItem !== false
    ? block?.lastPreviewedItem?.url
    : null;
}
